import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Button,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Slide,
  Skeleton,
  Tooltip,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { getFirmData } from "../../../../redux/Apps/Lawyerapp/DashboardSlice/DashboardSlice";
import { backend_api } from "../../../../static/server";
import {
  getAllAccounts,
  UpdateAccounts,
} from "../../../../redux/Apps/Lawyerapp/AccountSlice/AccountSlice";
import axios from "axios";
import { toast } from "react-toastify";
import { useDialog } from "../../../DialogContext";
import { LoadingButton } from "@mui/lab";
import { useSubscription } from "../../../Auth/SubscriptionContext";
import { LockRounded } from "@mui/icons-material";
import ContactApiField from "../../Settings/FieldsUi/ContactApiField";
import { buildCurrencyOptions } from "../../../../utils/currencyConfig";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const CreateAccountForm = ({ isEditMode, editId, formData }) => {
  const dispatch = useDispatch();
  const { closeDialog } = useDialog();
  const { isLimitReached } = useSubscription();
  const [openChangeDefaultDialog, setOpenChangeDefaultDialog] = useState(false);
  const [defaultAccount, setDefaultAccount] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { firmData } = useSelector((state) => state.dashboard);
  const {
    token,
    defaultCurrency,
    UserCurrencyList = [],
  } = useSelector((data) => data.user);
  const { AllAccounts } = useSelector((state) => state.account);
  const [fetching, setFetching] = useState(false);

  useEffect(() => {
    dispatch(
      getFirmData({
        url: `${backend_api}firm/create-law-firm/`,
        token: token,
      }),
    );
    dispatch(
      getAllAccounts({ url: `${backend_api}bank/accounts/`, token: token }),
    );
  }, [dispatch, token]);
  const onlyFirstFirm =
    Array.isArray(firmData) && firmData.length > 0 ? firmData[0] : null;

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      firm: onlyFirstFirm?.id,
      account_name: "",
      account_type: "",
      account_number: "",
      transit_routing_number: "",
      account_holder_name: "",
      institution: "",
      domicile_branch: "",
      swift_code: "",
      currency: "",
      balance: 0,
      is_default: false,
    },
  });

  const account_type = watch("account_type");
  const is_default = watch("is_default");
  const selectedCurrency = watch("currency");

  const currencyOptions = useMemo(
    () =>
      buildCurrencyOptions({
        userCurrencies: UserCurrencyList,
        includeValue: selectedCurrency || defaultCurrency,
      }),
    [UserCurrencyList, selectedCurrency, defaultCurrency],
  );

  useEffect(() => {
    const resolvedDefaultCurrency =
      selectedCurrency || defaultCurrency || currencyOptions[0]?.value || "";

    if (!selectedCurrency && resolvedDefaultCurrency) {
      setValue("currency", resolvedDefaultCurrency, { shouldDirty: false });
    }
  }, [currencyOptions, defaultCurrency, selectedCurrency, setValue]);
  useEffect(() => {
    const fetchAccountData = async () => {
      setFetching(true);
      try {
        const response = await axios.get(
          `${backend_api}bank/accounts/${editId}/`,
          {
            headers: {
              Authorization: `Token ${token}`,
            },
          },
        );

        if (response?.data) {
          Object.keys(response.data).forEach((key) => {
            setValue(key, response.data[key], { shouldDirty: true });
          });
        }
      } catch (error) {
        console.error("Error fetching account data:", error);
      } finally {
        setFetching(false);
      }
    };

    if (isEditMode && editId) {
      fetchAccountData();
    }
  }, [editId, isEditMode, setValue, token]);

  useEffect(() => {
    if (is_default && Array.isArray(AllAccounts)) {
      const foundAccount = AllAccounts?.find(
        (account) => account?.is_default === true,
      );

      if (foundAccount) {
        setDefaultAccount(foundAccount);

        if (foundAccount?.id !== editId) {
          setOpenChangeDefaultDialog(true);
        }
      }
    }
  }, [AllAccounts, editId, is_default]);

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    const body = {
      ...data,
      balance: 0,
      is_default:
        data?.account_type === "client fund" ? false : data?.is_default,
    };
    try {
      let response;

      if (isEditMode) {
        await dispatch(
          UpdateAccounts({
            url: `${backend_api}bank/accounts/${editId}/`,
            body: data,
            token: token,
          }),
        );
      } else {
        response = await axios.post(`${backend_api}bank/accounts/`, body, {
          headers: {
            Authorization: `Token ${token}`,
          },
        });

        // Only create opening balance transaction if the amount is not zero
        if (
          response?.data?.id &&
          data.balance &&
          parseFloat(data.balance) !== 0
        ) {
          const firstStatementData = {
            transaction_type: "deposit",
            account: response?.data?.id,
            amount: data?.balance,
            date: new Date().toLocaleDateString("en-US"),
            currency: response?.data?.currency,
            status: "completed",
            description: "Opening balance",
          };

          await axios.post(
            `${backend_api}bank/transactions/`,
            firstStatementData,
            {
              headers: {
                Authorization: `Token ${token}`,
              },
            },
          );
        }
      }

      toast.success(
        isEditMode
          ? "Account updated successfully"
          : "Account created successfully",
      );
    } catch (error) {
      toast.error(
        isEditMode ? "Failed to update account!" : "Failed to create account!",
      );
      console.error(
        "Error in onSubmit:",
        error.response?.data || error.message,
      );
    } finally {
      dispatch(getAllAccounts({ url: `${backend_api}bank/accounts/`, token }));
      reset();
      closeDialog();
      resetFormState();
      setIsSubmitting(false);
    }
  };

  const handleChangeDefaultAccount = async (data) => {
    try {
      const updatedData = {
        ...data,
        is_default: false,
        firm: data?.firm?.id,
        lawyer: data?.lawyer?.id,
      };

      const response = await dispatch(
        UpdateAccounts({
          url: `${backend_api}bank/accounts/${data?.id}/`,
          body: updatedData,
          token: token,
        }),
      );

      if (response?.error) {
        throw new Error(response.error);
      }
      toast.success("Default account updated successfully!");
    } catch (error) {
      console.error("Error updating default account:", error);
      toast.error("Failed to update the default account.");
    } finally {
      dispatch(
        getAllAccounts({ url: `${backend_api}bank/accounts/`, token: token }),
      );
      setOpenChangeDefaultDialog(false);
    }
  };

  const resetFormState = () => {
    reset({
      firm: onlyFirstFirm?.id,
      account_name: "",
      account_type: "",
      account_number: "",
      transit_routing_number: "",
      account_holder_name: "",
      institution: "",
      domicile_branch: "",
      swift_code: "",
      currency: "",
      balance: 0,
      is_default: false,
    });
  };

  return (
    <>
      <Box>
        {fetching ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
              p: "1rem",
              width: "100%",
            }}
          >
            <Box sx={{ display: "flex", gap: "1rem" }}>
              <Skeleton variant="rounded" width={"100%"} height={"2.5rem"} />
              <Skeleton variant="rounded" width={"100%"} height={"2.5rem"} />
            </Box>
            <Box sx={{ display: "flex", gap: "1rem" }}>
              <Skeleton variant="rounded" width={"100%"} height={"2.5rem"} />
              <Skeleton variant="rounded" width={"100%"} height={"2.5rem"} />
            </Box>
            <Skeleton variant="rounded" width={"100%"} height={"2.5rem"} />
            <Skeleton variant="rounded" width={"100%"} height={"2.5rem"} />
            <Skeleton variant="rounded" width={"100%"} height={"2.5rem"} />
            <Skeleton variant="rounded" width={"100%"} height={"2.5rem"} />
            <Skeleton variant="rounded" width={"100%"} height={"2.5rem"} />
            <Box sx={{ display: "flex", gap: "1rem" }}>
              <Skeleton variant="rounded" width={"100%"} height={"2.5rem"} />
              <Skeleton variant="rounded" width={"100%"} height={"2.5rem"} />
            </Box>
            <Box sx={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
              <Skeleton variant="rounded" width={20} height={20} />
              <Skeleton
                variant="text"
                sx={{ fontSize: "1rem", width: "40%" }}
              />
            </Box>
          </Box>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "1rem",
                p: "1rem",
              }}
            >
              <Box sx={{ display: "flex", gap: "1rem" }}>
                <Controller
                  name="account_name"
                  control={control}
                  rules={{
                    required: "Account name is required",
                    pattern: {
                      value: /^[a-zA-Z0-9\s-]+$/,
                      message:
                        "Only letters, numbers, spaces, and hyphens are allowed",
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={
                        <span>
                          Account Name<span style={{ color: "red" }}>*</span>
                        </span>
                      }
                      size="small"
                      fullWidth
                      error={!!errors?.account_name}
                      helperText={errors?.account_name?.message}
                    />
                  )}
                />
                <Controller
                  name="account_type"
                  control={control}
                  rules={{ required: "Account type is required" }}
                  render={({ field }) => (
                    <FormControl
                      size="small"
                      fullWidth
                      error={!!errors?.account_type}
                      disabled={isEditMode}
                    >
                      <InputLabel
                        id="account_type-label"
                        sx={{ bgcolor: "#fff", px: "0.3rem" }}
                      >
                        <span>
                          Account Type<span style={{ color: "red" }}>*</span>
                        </span>
                      </InputLabel>
                      <Select {...field} labelId="account_type-label">
                        <MenuItem value="law firm">Law Firm Account</MenuItem>
                        <MenuItem value="client fund">
                          Client fund Account
                        </MenuItem>
                      </Select>
                      {errors?.account_type && (
                        <FormHelperText>
                          {errors.account_type.message}
                        </FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
              </Box>
              <Box sx={{ display: "flex", gap: "1rem" }}>
                <Controller
                  name="account_number"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Account Number"
                      size="small"
                      fullWidth
                    />
                  )}
                />
                <Controller
                  name="transit_routing_number"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Transit/Routing Number"
                      size="small"
                      fullWidth
                    />
                  )}
                />
              </Box>
              <Controller
                name="account_holder_name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Account Holder Name"
                    size="small"
                    fullWidth
                  />
                )}
              />
              <Controller
                name="institution"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Institution"
                    size="small"
                    fullWidth
                  />
                )}
              />
              <Controller
                name="domicile_branch"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Domicile branch"
                    size="small"
                    fullWidth
                  />
                )}
              />
              <Controller
                name="swift_code"
                control={control}
                render={({ field }) => (
                  <TextField {...field} label="Swift" size="small" fullWidth />
                )}
              />
              <Box sx={{ display: "flex", gap: "1rem" }}>
                <Controller
                  name="currency"
                  control={control}
                  rules={{ required: "Currency is required" }}
                  render={({ field }) => (
                    <FormControl size="small" fullWidth>
                      <Autocomplete
                        size="small"
                        fullWidth
                        options={currencyOptions}
                        getOptionLabel={(option) => option?.label || ""}
                        value={
                          currencyOptions.find(
                            (option) => option?.value === field?.value,
                          ) || null
                        }
                        onChange={(_, newValue) =>
                          field.onChange(newValue ? newValue?.value : null)
                        }
                        isOptionEqualToValue={(option, value) =>
                          option?.value === value?.value
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={
                              <span>
                                Currency<span style={{ color: "red" }}>*</span>
                              </span>
                            }
                            size="small"
                            fullWidth
                            error={!!errors?.currency}
                            helperText={errors?.currency?.message}
                          />
                        )}
                      />
                    </FormControl>
                  )}
                />
                <Controller
                  name="balance"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      type="number"
                      label="Opening balance"
                      size="small"
                      fullWidth
                      error={!!error}
                      helperText={error ? error.message : ""}
                      disabled={isEditMode}
                    />
                  )}
                />
              </Box>
              {account_type && account_type === "client fund" && (
                <ContactApiField
                  name="client"
                  label="Client"
                  control={control}
                  errors={errors}
                  token={token}
                  apiUrl={`${backend_api}contact/retrieve/`}
                  isRequired
                />
              )}
              {account_type !== "client fund" && (
                <Controller
                  name="is_default"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Checkbox {...field} checked={field.value} />}
                      label="Default Account"
                      sx={{
                        "& .MuiFormControlLabel-label": {
                          fontSize: "0.8rem",
                          fontWeight: 500,
                          color: "text.secondary",
                        },
                      }}
                    />
                  )}
                />
              )}
            </Box>
            <Box
              sx={{
                height: "fit-content",
                borderTop: "1px solid #ddd",
                position: "sticky",
                bottom: 0,
                backdropFilter: "blur(10px)",
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                p: "0.5rem",
                zIndex: 1000,
                gap: "0.5rem",
                bgcolor: "rgba(255, 255, 255, 0.8)",
              }}
            >
              <Button variant="outlined" size="small" onClick={closeDialog}>
                Cancel
              </Button>

              {/* Determine which limit to check */}
              <Tooltip
                title={
                  account_type === "client fund"
                    ? isLimitReached("current_client_account_limit")
                      ? "You’ve reached your Client Account limit. Upgrade your plan to add more Client Accounts."
                      : ""
                    : account_type === "law firm"
                      ? isLimitReached("current_firm_account_limit")
                        ? "You’ve reached your Law Firm Account limit. Upgrade your plan to add more Law Firm Accounts."
                        : ""
                      : ""
                }
                disableHoverListener={
                  (account_type === "client fund" &&
                    !isLimitReached("current_client_account_limit")) ||
                  (account_type === "law firm" &&
                    !isLimitReached("current_firm_account_limit"))
                }
              >
                <span>
                  <LoadingButton
                    loading={isSubmitting}
                    variant="contained"
                    color="primary"
                    type="submit"
                    size="small"
                    disabled={
                      (account_type === "client fund" &&
                        isLimitReached("current_client_account_limit")) ||
                      (account_type === "law firm" &&
                        isLimitReached("current_firm_account_limit"))
                    }
                    sx={{
                      textTransform: "capitalize",
                      cursor:
                        (account_type === "client fund" &&
                          isLimitReached("current_client_account_limit")) ||
                        (account_type === "law firm" &&
                          isLimitReached("current_firm_account_limit"))
                          ? "not-allowed"
                          : "pointer",
                      opacity:
                        (account_type === "client fund" &&
                          isLimitReached("current_client_account_limit")) ||
                        (account_type === "law firm" &&
                          isLimitReached("current_firm_account_limit"))
                          ? 0.8
                          : 1,
                    }}
                    endIcon={
                      (account_type === "client fund" &&
                        isLimitReached("current_client_account_limit")) ||
                      (account_type === "law firm" &&
                        isLimitReached("current_firm_account_limit")) ? (
                        <LockRounded
                          sx={{ fontSize: "1rem", color: "error.main" }}
                        />
                      ) : null
                    }
                  >
                    {(account_type === "client fund" &&
                      isLimitReached("current_client_account_limit")) ||
                    (account_type === "law firm" &&
                      isLimitReached("current_firm_account_limit"))
                      ? "Limit Reached"
                      : isEditMode
                        ? "Update Account"
                        : "Create Account"}
                  </LoadingButton>
                </span>
              </Tooltip>
            </Box>
          </form>
        )}
      </Box>

      {/* ---------------------------- CHANGE DEFAULT ACCOUNT --------------------------- */}
      <Dialog
        open={openChangeDefaultDialog}
        TransitionComponent={Transition}
        keepMounted
        onClose={() => {
          setValue("is_default", false);
          setOpenChangeDefaultDialog(false);
        }}
        sx={{ zIndex: "2000" }}
      >
        <DialogTitle>Change Default Account</DialogTitle>
        <DialogContent>
          <Typography>
            You already have a default account. If you want to change it, please
            disable the current default account first.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenChangeDefaultDialog(false);
            }}
            color="secondary"
          >
            Cancel
          </Button>

          <Button
            color="primary"
            onClick={() => handleChangeDefaultAccount(defaultAccount)}
          >
            Change Default Account
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CreateAccountForm;
